import React, { useMemo, useState } from "react";

import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import TuneIcon from "@mui/icons-material/Tune";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const DIETARY_OPTIONS = [
  {
    label: "Veg",
    value: "VEG",
    color: "#16B913",
  },
  {
    label: "Non-Veg",
    value: "NON_VEG",
    color: "#C90000",
  },
  {
    label: "Vegan",
    value: "VEGAN",
    color: "#078D80",
  },
];

const SORT_OPTIONS = [
  {
    label: "Price: Low to High",
    value: "PRICE_LOW_HIGH",
  },
  {
    label: "Price: High to Low",
    value: "PRICE_HIGH_LOW",
  },
  // {
  //   label: "Preparation Time",
  //   value: "PREPARATION_TIME",
  // },
];

const DietaryIcon = ({ value, color }) => {
  return (
    <Box
      sx={{
        width: {
          xs: 29,
          sm: 31,
        },
        height: {
          xs: 29,
          sm: 31,
        },
        flexShrink: 0,
        borderRadius: {
          xs: "8px",
          sm: "9px",
        },
        border: {
          xs: `2.25px solid ${color}`,
          sm: `2.5px solid ${color}`,
        },
        backgroundColor: "#FFFFFF",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
        boxShadow: "0 2px 6px rgba(16,24,40,0.10)",
      }}
    >
      {value === "VEG" && (
        <Box
          sx={{
            width: {
              xs: 13,
              sm: 14,
            },
            height: {
              xs: 13,
              sm: 14,
            },
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />
      )}

      {value === "NON_VEG" && (
        <Box
          sx={{
            width: {
              xs: 14,
              sm: 15,
            },
            height: {
              xs: 13,
              sm: 14,
            },
            backgroundColor: color,
            borderRadius: "50% 50% 25% 25%",
            clipPath: "polygon(50% 0%, 100% 78%, 90% 100%, 10% 100%, 0% 78%)",
          }}
        />
      )}

      {value === "VEGAN" && (
        <Typography
          component="span"
          sx={{
            color,
            fontSize: {
              xs: 16,
              sm: 17,
            },
            lineHeight: 1,
            fontWeight: 900,
          }}
        >
          V
        </Typography>
      )}
    </Box>
  );
};

const DietaryOptionTrack = ({ option, selected, onClick }) => {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`${selected ? "Remove" : "Select"} ${option.label} filter`}
      aria-pressed={selected}
      sx={{
        width: "100%",
        minWidth: 0,
        border: 0,
        outline: 0,
        p: 0,
        cursor: "pointer",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: {
            xs: 35,
            sm: 39,
          },
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Slightly narrower individual track */}
        <Box
          sx={{
            position: "absolute",
            left: {
              xs: 17,
              sm: 19,
            },
            right: {
              xs: 17,
              sm: 19,
            },
            height: {
              xs: 13,
              sm: 15,
            },
            borderRadius: "16px",
            backgroundColor: selected ? option.color : "#FFFFFF",
            border: selected
              ? `1px solid ${option.color}`
              : "1px solid #EAECF0",
            boxShadow: selected
              ? `0 2px 6px ${option.color}33`
              : "0 1px 4px rgba(16,24,40,0.08)",
            transition:
              "background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
          }}
        />

        {/* Moving icon */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: selected
              ? {
                  xs: "calc(100% - 18px)",
                  sm: "calc(100% - 20px)",
                }
              : {
                  xs: "18px",
                  sm: "20px",
                },
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            transition: "left 260ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <DietaryIcon value={option.value} color={option.color} />
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 0.1,
          fontSize: {
            xs: 9,
            sm: 9.5,
          },
          lineHeight: 1.2,
          fontWeight: selected ? 800 : 600,
          color: selected ? option.color : "#98A2B3",
          textAlign: "center",
          transition: "color 180ms ease",
        }}
      >
        {option.label}
      </Typography>
    </Box>
  );
};

const DietaryTrackSelector = ({ value = "ALL", onChange }) => {
  const handleOptionClick = (optionValue) => {
    /*
     * ALL is not shown in the UI.
     *
     * Clicking an inactive option selects it.
     * Clicking the active option again clears it
     * and internally restores ALL.
     */
    const nextValue = value === optionValue ? "ALL" : optionValue;

    onChange?.(nextValue);
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: {
          xs: 0.75,
          sm: 0.9,
        },
        borderRadius: {
          xs: "18px",
          sm: "22px",
        },
        backgroundColor: "#E2F0F5",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: {
            xs: 0.55,
            sm: 0.7,
          },
          alignItems: "start",
        }}
      >
        {DIETARY_OPTIONS.map((option) => (
          <DietaryOptionTrack
            key={option.value}
            option={option}
            selected={value === option.value}
            onClick={() => handleOptionClick(option.value)}
          />
        ))}
      </Box>
    </Box>
  );
};

const ProductFilterPopover = ({
  dietaryFilter = "ALL",
  sortOption = "DEFAULT",
  onDietaryFilterChange,
  onSortChange,
  onClear,
}) => {
  const [anchorElement, setAnchorElement] = useState(null);

  const open = Boolean(anchorElement);

  const activeFilterCount = useMemo(() => {
    return Number(dietaryFilter !== "ALL") + Number(sortOption !== "DEFAULT");
  }, [dietaryFilter, sortOption]);

  const handleOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleDietaryChange = (value) => {
    onDietaryFilterChange?.(value);
  };

  const handleSortChange = (event) => {
    onSortChange?.(event.target.value);
  };

  const handleClear = () => {
    /*
     * Parent onClear should reset:
     * dietaryFilter to ALL
     * sortOption to DEFAULT
     */
    onClear?.();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label="Filter and sort products"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          width: 35,
          height: 35,
          position: "relative",
          backgroundColor: activeFilterCount > 0 ? "#09A5F7" : "transparent",
          color: activeFilterCount > 0 ? "#FFFFFF" : "#09A5F7",
          transition:
            "background-color 150ms ease, color 150ms ease, transform 150ms ease",

          "&:hover": {
            backgroundColor:
              activeFilterCount > 0 ? "#078FD0" : "rgba(9,165,247,0.10)",
          },

          "&:active": {
            transform: "scale(0.94)",
          },
        }}
      >
        <TuneIcon
          sx={{
            fontSize: 21,
          }}
        />

        {activeFilterCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 17,
              height: 17,
              px: 0.35,
              borderRadius: "50%",
              backgroundColor: "#FFE600",
              color: "#171717",
              border: "2px solid #FFFFFF",
              fontSize: 9,
              lineHeight: 1,
              fontWeight: 900,
              display: "grid",
              placeItems: "center",
              boxSizing: "border-box",
            }}
          >
            {activeFilterCount}
          </Box>
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,

              // Horizontally center on screen
              left: "50% !important",
              transform: "translateX(-50%) !important",

              width: {
                xs: "calc(100vw - 24px)",
                sm: 380,
              },
              maxWidth: 380,
              maxHeight: "calc(100dvh - 100px)",
              borderRadius: {
                xs: "18px",
                sm: "22px",
              },
              border: "1px solid #EAECF0",
              boxShadow: "0 18px 50px rgba(16,24,40,0.20)",
              overflowY: "auto",
            },
          },
        }}
      >
        <Box
          sx={{
            p: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{
              mb: {
                xs: 1,
                sm: 1.15,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 800,
                color: "#344054",
              }}
            >
              Filters
            </Typography>

            <Button
              size="small"
              disabled={activeFilterCount === 0}
              onClick={handleClear}
              aria-label="Clear filters"
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.4,
                textTransform: "none",
                color: "#09A5F7",
                borderRadius: "8px",

                "&:hover": {
                  backgroundColor: "rgba(9,165,247,0.08)",
                },

                "&.Mui-disabled": {
                  color: "#98A2B3",
                },
              }}
            >
              Clear filters
            </Button>
          </Stack>

          <DietaryTrackSelector
            value={dietaryFilter}
            onChange={handleDietaryChange}
          />

          <Divider
            sx={{
              my: {
                xs: 1.4,
                sm: 1.7,
              },
            }}
          />

          <Typography
            sx={{
              mb: 0.6,
              fontSize: 13,
              fontWeight: 800,
              color: "#344054",
            }}
          >
            Sort products
          </Typography>

          <RadioGroup value={sortOption} onChange={handleSortChange}>
            {SORT_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: "#98A2B3",

                      "&.Mui-checked": {
                        color: "#09A5F7",
                      },
                    }}
                  />
                }
                label={option.label}
                sx={{
                  mx: 0,
                  minHeight: {
                    xs: 38,
                    sm: 40,
                  },
                  px: 0.4,
                  borderRadius: "10px",
                  backgroundColor:
                    sortOption === option.value
                      ? "rgba(9,165,247,0.06)"
                      : "transparent",
                  transition: "background-color 150ms ease",

                  "&:hover": {
                    backgroundColor:
                      sortOption === option.value
                        ? "rgba(9,165,247,0.09)"
                        : "#F9FAFB",
                  },

                  "& .MuiFormControlLabel-label": {
                    fontSize: 12.5,
                    fontWeight: sortOption === option.value ? 700 : 500,
                    color: "#344054",
                  },
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Popover>
    </>
  );
};

export default ProductFilterPopover;
